import useScrollReveal from '../useScrollReveal';
import useGsapReveal from '../useGsapReveal';
import { sectionStyles, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function ProcessSection({ block = {}, primaryColor, secondaryColor, enableAnimations }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const items = content.items || [];
    const variant = config.variant || 'horizontal';
    const accent = config.accent_color || primaryColor;
    const gsapRef = useGsapReveal({ animation: 'fade-up', disabled: !enableAnimations });

    if (items.length === 0) return null;

    const sectionCls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;

    // Zigzag variant
    if (variant === 'zigzag') {
        return (
            <section className={sectionCls} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className="relative max-w-4xl mx-auto">
                        {/* Connecting line */}
                        <div className="absolute left-1/2 top-0 bottom-0 w-0.5 -translate-x-1/2 hidden md:block" style={{ backgroundColor: hexToRgba(accent, 0.13) }} />
                        <div className="space-y-12 md:space-y-0">
                            {items.map((item, i) => {
                                const isLeft = i % 2 === 0;
                                return (
                                    <div key={i}
                                        className={`flex flex-col md:flex-row items-center gap-6 md:gap-0 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                        style={{ transitionDelay: visible ? `${i * 150}ms` : '0ms' }}>
                                        {/* Left content */}
                                        <div className={`md:w-5/12 ${isLeft ? 'md:text-right md:pr-8' : 'md:order-3 md:text-left md:pl-8'}`}>
                                            <h4 className="text-lg font-bold mb-1" style={{ color: s.text }}>{item.title}</h4>
                                            {item.description && <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{item.description}</p>}
                                            {item.link && <a {...resolveLinkProps(item.link)} className="inline-flex items-center gap-1 text-sm font-medium mt-2 hover:underline transition-colors" style={{ color: s.accent }}>{item.link_text || 'Ver mas'}</a>}
                                        </div>
                                        {/* Center circle */}
                                        <div className="md:w-2/12 flex justify-center md:order-2 relative z-10">
                                            <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg"
                                                style={{ backgroundColor: item.color || accent }}>
                                                {item.icon || (i + 1)}
                                            </div>
                                        </div>
                                        {/* Right spacer */}
                                        <div className={`hidden md:block md:w-5/12 ${isLeft ? 'md:order-3' : ''}`} />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color="transparent"  config={config}/>
            </section>
        );
    }

    // Cards variant
    if (variant === 'cards') {
        return (
            <section className={sectionCls} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {items.map((item, i) => (
                            <div key={i}
                                className={`rounded-2xl p-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : hexToRgba(accent, 0.04), transitionDelay: visible ? `${i * 120}ms` : '0ms' }}>
                                <span className="text-3xl font-bold leading-none block mb-3" style={{ color: hexToRgba(accent, 0.7) }}>
                                    {String(i + 1).padStart(2, '0')}
                                </span>
                                <h4 className="text-base font-bold mb-2" style={{ color: s.text }}>{item.title}</h4>
                                {item.description && <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{item.description}</p>}
                                {item.link && <a {...resolveLinkProps(item.link)} className="inline-flex items-center gap-1 text-sm font-medium mt-3 hover:underline transition-colors" style={{ color: s.accent }}>{item.link_text || 'Ver mas'}</a>}
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color="transparent"  config={config}/>
            </section>
        );
    }

    // Journey variant
    if (variant === 'journey') {
        return (
            <section className={sectionCls} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`relative z-10 ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} primaryColor={primaryColor}/>
                    <div className="relative max-w-4xl mx-auto">
                        {/* Vertical connecting line (desktop) */}
                        <div className="absolute left-6 lg:left-8 top-0 bottom-0 w-0.5 hidden md:block" style={{ backgroundColor: hexToRgba(accent, 0.15) }} />
                        <div className="space-y-10 md:space-y-12">
                            {items.map((item, i) => (
                                <div key={i}
                                    className={`flex flex-col md:flex-row items-start gap-4 md:gap-8 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                    style={{ transitionDelay: visible ? `${i * 150}ms` : '0ms' }}>
                                    {/* Large number */}
                                    <div className="relative z-10 shrink-0 flex items-center justify-center md:w-16 lg:w-20">
                                        <span className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-none select-none" style={{ color: hexToRgba(accent, 0.18) }}>
                                            {String(i + 1).padStart(2, '0')}
                                        </span>
                                    </div>
                                    {/* Content */}
                                    <div className="flex-1 pb-2">
                                        <h4 className="text-lg font-bold mb-1" style={{ color: s.text }}>{item.title}</h4>
                                        {item.description && <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{item.description}</p>}
                                        {item.link && <a {...resolveLinkProps(item.link)} className="inline-flex items-center gap-1 text-sm font-medium mt-2 hover:underline transition-colors" style={{ color: s.accent }}>{item.link_text || 'Ver mas'}</a>}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {/* Horizontal dots connector (mobile) */}
                        <div className="flex items-center justify-center gap-2 mt-6 md:hidden">
                            {items.map((_, i) => (
                                <div key={i} className="w-2 h-2 rounded-full" style={{ backgroundColor: hexToRgba(accent, i === 0 ? 0.8 : 0.3) }} />
                            ))}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // Horizontal variant (default)
    return (
        <section className={sectionCls} style={s.bgStyle}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                {content.title && <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                <div ref={gsapRef} className="flex flex-col md:flex-row items-start justify-center gap-0">
                    {items.map((item, i) => (
                        <div key={i} className="flex flex-col md:flex-row items-center flex-1">
                            {/* Step */}
                            <div className={`flex flex-col items-center text-center px-4 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                style={{ transitionDelay: visible ? `${i * 150}ms` : '0ms' }}>
                                <div className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg mb-4"
                                    style={{ backgroundColor: item.color || accent }}>
                                    {item.icon || (i + 1)}
                                </div>
                                <h4 className="text-base font-bold mb-1" style={{ color: s.text }}>{item.title}</h4>
                                {item.description && (
                                    <p className="text-sm leading-relaxed max-w-[200px]" style={{ color: s.textSub }}>{item.description}</p>
                                )}
                                {item.link && <a {...resolveLinkProps(item.link)} className="inline-flex items-center gap-1 text-sm font-medium mt-2 hover:underline transition-colors" style={{ color: s.accent }}>{item.link_text || 'Ver mas'}</a>}
                            </div>
                            {/* Arrow */}
                            {i < items.length - 1 && (
                                <div className={`py-3 md:py-0 md:px-2 shrink-0 transition-all duration-500 ${visible ? 'opacity-100' : 'opacity-0'}`}
                                    style={{ transitionDelay: visible ? `${i * 150 + 100}ms` : '0ms' }}>
                                    {/* Vertical arrow (mobile) */}
                                    <svg className="w-6 h-6 md:hidden" style={{ color: accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                    </svg>
                                    {/* Horizontal arrow (desktop) */}
                                    <svg className="w-8 h-8 hidden md:block" style={{ color: accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                    </svg>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color="transparent"  config={config}/>
        </section>
    );
}
